import React, { useEffect, useCallback } from 'react';

import axios from 'axios';
import { useDispatch } from 'react-redux';

import { setSnackbarMessage } from 'actions/snackbar';
import { updateDeviceInfo } from 'actions/userDevice';

const AXIOS_CLIENT_HEADERS = {
  'Content-Type': 'application/json',
  'Cache-Control': 'no-cache',
  Pragma: 'no-cache',
  Expires: '0',
};

const useAppConfig = () => {
  const dispatch = useDispatch();

  const onWindowFocus = useCallback(() => {
    const clientVersion = VERSION;
    const axiosClient = axios.create({
      headers: AXIOS_CLIENT_HEADERS,
    });

    axiosClient
      .get(`${window.location.origin}/assets-alpha/meta.json`)
      .then(response => {
        if ([200, 304].includes(response.status)) {
          const remoteVersion = response?.data?.version ?? '';
          console.info(
            `client version ${clientVersion}, remote version: ${remoteVersion}`,
          );
          if (Boolean(remoteVersion) && `${remoteVersion}` !== clientVersion) {
            dispatch(
              setSnackbarMessage({
                message: (
                  <span>
                    New updates found,{' '}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        window.location.reload(true);
                      }}
                    >
                      click here to refresh
                    </span>
                    .
                  </span>
                ),
              }),
            );
          }
        }
      });
  }, [dispatch]);

  const handleMobileResize = () => {
    // If the device's native screen width is less than 400px, then set the
    // app's viewport scale to 0.8 which allows us to show more content on
    // mobile devices.
    if (window.screen.width < 400) {
      const deviceViewport = document.getElementById('device-viewport');
      /*
        We check if deviceViewport exists because:
        Viewport Units: Vw, Vh, Vmin, Vmax on Chrome for Android is fully supported on 97-103, 
        partially supported on None of the versions, and not supported on below 97 Chrome for Android versions.
        Article: https://www.lambdatest.com/web-technologies/viewport-units
      */
      if (deviceViewport) {
        deviceViewport.setAttribute(
          'content',
          'width=device-width,initial-scale=.8',
        );
      }
    }
  };

  /** 
    When app mounts, we need to update current user device and add 3 event listeners for the responsiveness and focus events
    @onWindowFocus -> Triggers when app loads immediately, handles to show user a message if we did a deployment and there's a new version
    @handleMobileResize -> Controls mobile responsiveness and dynamic css styling on window resizing when app loads & window being resized
  */
  useEffect(() => {
    dispatch(updateDeviceInfo());
    window.addEventListener('focus', onWindowFocus);
    window.addEventListener('load', handleMobileResize);
    window.addEventListener('resize', handleMobileResize);
  }, [dispatch, onWindowFocus]);

  /*
    When we close the app, we need to unmount these handlers
   */
  useEffect(
    () => () => {
      window.removeEventListener('focus', onWindowFocus);
      window.removeEventListener('load', handleMobileResize);
      window.removeEventListener('resize', handleMobileResize);
    },
    [onWindowFocus],
  );
};

export default useAppConfig;
